import { Box } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';

import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useUser } from '../../../../global/auth/useUser';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { MyTasks, NewTaskListItemContent, CompletedTaskListItemContent } from './MyTasks';
import { GET_MY_TASKS } from './my-tasks-model.gql';
import { GetMyTasks, GetMyTasksVariables } from './__generated__/GetMyTasks';

interface TasksDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  initialTaskId?: string;
  singleTaskId?: string;
  onTaskCompleted?: () => void;
}
const useStyles = makeStyles(() =>
  createStyles({
    dialogPaper: { overflow: 'visible', minHeight: '90vh' },
    singleDialogPaper: { overflow: 'visible' },
  })
);

const SingleTaskContent = ({
  taskId,
  onClose,
  onTaskCompleted,
}: {
  taskId: string;
  onClose: () => void;
  onTaskCompleted?: () => void;
}) => {
  const { userIdentity } = useUser();
  const [isActionCancelled, setIsActionCancelled] = React.useState(false);

  const { data, refetch } = useCampfireQuery<GetMyTasks, GetMyTasksVariables>(GET_MY_TASKS, {
    options: { variables: { userId: userIdentity.userId, bulletinView: 'all' } },
  });

  React.useEffect(() => {
    if (isActionCancelled) {
      setIsActionCancelled(false);
      onClose();
    }
  }, [isActionCancelled]);

  const myProfile = data?.vm.profile ?? null;
  const task = (data?.vm.cakes ?? []).flatMap((c) => c.tasks ?? []).find((t) => t.taskId === taskId);
  const isCompleted = !!myProfile?.completedTasks.find((ct) => ct.task.taskId === taskId);

  if (!task) return null;

  const refetchAll = () => {
    if (refetch) refetch();
  };

  return isCompleted ? (
    <CompletedTaskListItemContent myProfile={myProfile} task={task} refetchMyTasks={refetchAll} />
  ) : (
    <NewTaskListItemContent
      task={task}
      setIsActionCancelled={setIsActionCancelled}
      refetchMyTasks={() => {
        refetchAll();
        if (onTaskCompleted) onTaskCompleted();
        onClose();
      }}
      volunteerProgramIds={myProfile?.programs.map((p) => p.programId)}
    />
  );
};

const TasksDialog = (props: TasksDialogProps) => {
  const { open, setOpen, initialTaskId, singleTaskId, onTaskCompleted } = props;
  const { isMobile } = useCampfireTheme();
  const classes = useStyles();

  function onClose() {
    setOpen(false);
  }

  if (singleTaskId) {
    return (
      <FullscreenDialog
        open={open}
        close={onClose}
        fullScreen={isMobile}
        title='Task'
        PaperProps={{ className: classes.singleDialogPaper }}
        maxWidth='sm'
        fullWidth
      >
        <Box py='24px' px='8px'>
          <SingleTaskContent taskId={singleTaskId} onClose={onClose} onTaskCompleted={onTaskCompleted} />
        </Box>
      </FullscreenDialog>
    );
  }

  return (
    <FullscreenDialog
      open={open}
      close={onClose}
      fullScreen={isMobile}
      title='Tasks'
      subtitle='These tasks below have been assigned to you to complete.'
      PaperProps={{ className: classes.dialogPaper }}
      maxWidth='sm'
      fullWidth
    >
      <Box py='24px'>
        <MyTasks initialExpandedTaskId={initialTaskId} />
      </Box>
    </FullscreenDialog>
  );
};

export { TasksDialog };
